// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useCallback, useEffect, useState } from "react";
import BackdropProgress from "../../BackdropProgress.js";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import CustomSnackbar from "../ui/Snackbars.js";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import EnhancedTableHead from "../ui/EnhancedTableHead.js";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import Checkbox from "@mui/material/Checkbox";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import useHttp from "../../utils/http.js";
import { travelStatus } from "../../utils/const.js";
import wso2Theme from "../../theme/index.js";
import {
  getComparator,
  stableSort,
  formatDate,
  formatAmount,
} from "../../utils/formatting.js";
import { getEmployeeEmails } from "../../utils/oauth.js";
import { Box } from "@material-ui/core";
import {
  perdiemPaymentNetsuiteStatus,
  perdiemPaymentNetsuiteStatusArray,
  perdiemPaymentNetSuiteStatusLabels,
  order as sortOrder,
  orderBy as sortOrderBy,
} from "../../utils/const.js";
import {
  postSearchPerdiemPayments,
  postPushPerdiemsPayments,
  getPaymentCurrencyFilters,
} from "../../utils/end-points.js";
import {
  getNetSuiteStatusInfo,
  getPerdiemPaymentNetSuiteStatusInfo,
} from "../../theme/status-colors.js";
import { isAccessGranted } from "../../utils/oauth.js";
import { PRIVILEGES } from "../../Config.js";
import ConfirmationDialog from "../dialog/ConfirmationDialog.js";
import "../../css/AllPerDiemPayments.scss";

const headCells = [
  {
    id: "checkbox",
  },
  {
    id: "per_diem_number",
    label: "Per Diem Payment Number",
    align: "left",
    disablePadding: false,
    sortable: true,
  },
  {
    id: "traveller_email",
    label: "Traveller's Email",
    align: "left",
    disablePadding: false,
    sortable: true,
  },
  {
    id: "added_date",
    label: "Payment Added Date",
    align: "left",
    disablePadding: false,
    sortable: true,
  },
  {
    id: "payment_currency",
    label: "Payment Currency",
    align: "left",
    disablePadding: false,
  },
  {
    id: "payment_amount",
    label: "Payment Amount",
    align: "left",
    disablePadding: false,
  },
  {
    id: "converted_currency",
    label: "Converted Currency",
    align: "left",
    disablePadding: false,
  },
  {
    id: "converted_amount",
    label: "Converted Amount",
    align: "left",
    disablePadding: false,
  },
  {
    id: "netsuite_status",
    label: "NS Status",
    align: "left",
    disablePadding: false,
  },
];

const AllPerDiemPayments = () => {
  const { handleRequest } = useHttp();
  const employees = getEmployeeEmails();
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [payments, setPayments] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchFilter, setSearchFilter] = useState("");
  const [order, setOrder] = useState(sortOrder.DESC);
  const [orderBy, setOrderBy] = useState(sortOrderBy.ADDED_DATE);
  const [isTablePaginating, setTablePaginating] = useState(false);
  const [emailFilter, setEmailFilter] = useState(null);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState(
    perdiemPaymentNetSuiteStatusLabels.PUSHED
  );
  const [currencyFilter, setCurrencyFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [isTableLoading, setTableLoading] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isPageLoading, setPageLoading] = useState(true);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [confirmationDialogData, setConfirmationDialogData] = useState({
    open: false,
    title: "",
    content: "",
    acceptLabel: "",
    declineLabel: "",
    acceptAlert: null,
    declineAlert: null,
  });
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "",
    onClose: null,
  });

  useEffect(() => {
    if (window.innerHeight >= 850) {
      setRowsPerPage(12);
    }
  }, []);

  useEffect(() => {
    fetchPerdiemData();
  }, [
    rowsPerPage,
    searchFilter,
    order,
    orderBy,
    emailFilter,
    statusFilter,
    currencyFilter,
  ]);

  useEffect(() => {
    fetchPaymentCurrencyFilters();
  }, []);

  const fetchPerdiemData = useCallback(
    async (showSuccessSnack) => {
      setTableLoading(true);

      let netsuiteStatusFilter = getPerdiemPaymentStatusByFilters();
      let body = {
        limit: rowsPerPage,
        offset: 0,
        searchString: searchFilter,
        sortColumn: orderBy,
        sortOrder: order,
        netsuiteStatus: netsuiteStatusFilter,
        travellerEmail: emailFilter?.workEmail,
        currency: currencyFilter,
      };

      handleRequest(
        postSearchPerdiemPayments,
        "POST",
        body,
        (response) => {
          if (response) {
            setPayments(response);
            setTableLoading(false);
            setPage(0);
            if (showSuccessSnack) {
              setSnackbarData({
                message: "Success",
                open: true,
                severity: "success",
                onClose: onCloseSnackbar,
              });
            }
          } else {
            setSnackbarData({
              message: "Error while fetching per diem payments",
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
          }
          setPageLoading(false);
        },
        (error) => {
          setPageLoading(false);
          setSnackbarData({
            message: error,
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      );
    },
    [
      rowsPerPage,
      searchFilter,
      order,
      orderBy,
      emailFilter,
      statusFilter,
      currencyFilter,
    ]
  );

  const loadMorePerdiemPayments = (offset) => {
    setTablePaginating(true);

    let netsuiteStatusFilter = getPerdiemPaymentStatusByFilters();
    let body = {
      limit: rowsPerPage,
      offset: offset,
      searchString: searchFilter,
      sortColumn: orderBy,
      sortOrder: order,
      netsuiteStatus: netsuiteStatusFilter,
      travellerEmail: emailFilter?.workEmail,
      currency: currencyFilter,
    };
    handleRequest(
      postSearchPerdiemPayments,
      "POST",
      body,
      (response) => {
        if (response) {
          let perdiemList = payments.concat(response);
          setPayments(perdiemList);
          if (response.length === 0) {
            setPage((page) => page - 1);
            setSnackbarData({
              message: "No more per diem payments",
              open: true,
              severity: "success",
              onClose: onCloseSnackbar,
            });
          }
        } else {
          setSnackbarData({
            message: "Error while fetching per diem payments",
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
        setTablePaginating(false);
      },
      (error) => {
        setTablePaginating(false);
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    );
  };

  const fetchPaymentCurrencyFilters = () => {
    handleRequest(
      getPaymentCurrencyFilters,
      "GET",
      null,
      (response) => {
        if (response && response.length > 0) {
          let currencies = response.map((item) => item.currency);
          setCurrencyOptions(currencies);
        }
        setPageLoading(false);
      },
      (error) => {
        setPageLoading(false);
      }
    );
  };

  const getPerdiemPaymentStatusByFilters = () => {
    if (statusFilter === perdiemPaymentNetSuiteStatusLabels.PUSHED) {
      return perdiemPaymentNetsuiteStatus.COMPLETE;
    } else if (statusFilter === perdiemPaymentNetSuiteStatusLabels.PENDING) {
      return perdiemPaymentNetsuiteStatus.PENDING;
    } else {
      return null;
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === sortOrder.ASC;
    setOrder(isAsc ? sortOrder.DESC : sortOrder.ASC);
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    if (payments.length <= newPage * rowsPerPage) {
      loadMorePerdiemPayments(payments.length);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchFilter = (event) => {
    setSearchFilter(event.target.value);
    if (!event.target.value) {
      fetchPerdiemData();
    }
  };

  const handleEmailFilter = (event, value) => {
    setEmailFilter(value);
  };

  const handleDeclineAlert = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
  };

  const onCloseSnackbar = () => {
    setSnackbarData({ ...snackbarData, open: false });
  };

  const togglePaymentSelection = (paymentId) => {
    if (!paymentId) {
      return;
    }
    if (selectedPayments.includes(paymentId)) {
      setSelectedPayments(
        selectedPayments.filter((payment) => payment !== paymentId)
      );
    } else {
      setSelectedPayments((prev) => [...prev, paymentId]);
    }
  };

  const isSelectDisabled = (row) => {
    const isTravelInProgressOrExtended =
      row.travelStatus === travelStatus.INPROGRESS ||
      row.travelStatus === travelStatus.EXTENDED;
    const hasPushPerdiemsAccess = isAccessGranted([
      PRIVILEGES.PUSH_PER_DIEM_PAYMENTS_TO_NS,
    ]);

    return (
      !isTravelInProgressOrExtended ||
      !hasPushPerdiemsAccess ||
      row.netsuiteStatus === perdiemPaymentNetsuiteStatus.COMPLETE ||
      row.perDiemNumber === null
    );
  };

  const getNetSuiteStatus = (payment) => {
    let statusChip = getNetSuiteStatusInfo(payment.netsuiteStatus);
    return (
      <span>
        <span
          className="Status-chip"
          style={{
            backgroundColor: statusChip[1],
            color: statusChip[2],
          }}
        >
          {statusChip[0]}
        </span>
      </span>
    );
  };

  const handlePushPerdiemPaymentsToNS = () => {
    setConfirmationDialogData({
      open: true,
      title: "Push Per Diem Payments To NetSuite",
      content: `Are you sure you want to push the ${selectedPayments.length} selected per diem payment(s) to NetSuite?  
                Please note that this action cannot be undone.`,
      acceptLabel: "Yes",
      declineLabel: "No",
      acceptAlert: handlePushPerdiemPayments,
      declineAlert: handleDeclineAlert,
    });
  };

  const handlePushPerdiemPayments = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    setLoading(true);
    handleRequest(
      postPushPerdiemsPayments,
      "POST",
      selectedPayments,
      (paymentsResult) => {
        if (paymentsResult) {
          if (paymentsResult.failedPerDiemPaymentIds?.length > 0) {
            setSnackbarData({
              message: `Error while pushing following per diem payments to Net Suite: 
                        ${paymentsResult.failedPerDiemPaymentIds.join(", ")}`,
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
            setSelectedPayments([]);
            fetchPerdiemData(false);
            setLoading(false);
          } else {
            setSelectedPayments([]);
            fetchPerdiemData(true);
            setLoading(false);
          }
        } else {
          setLoading(false);
          setSnackbarData({
            message: "Error while pushing per diem payments to Net Suite",
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      },
      (error) => {
        setLoading(false);
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    );
  };

  return (
    <div className="All-per-diem-payment-information">
      <ConfirmationDialog confirmationDialogData={confirmationDialogData} />
      <CustomSnackbar snackbar={snackbarData} />
      <BackdropProgress open={isPageLoading || isLoading} />

      <div className="Page-title">All Per Diem Payments</div>

      {!isPageLoading && (
        <Grid container>
          <Grid item lg={12} className="Table-grid">
            <Paper className="Border-elevation">
              <div
                className="Search-and-filters"
                style={{ borderColor: wso2Theme.palette.primary.light }}
              >
                <div className="Table-search-field">
                  <TextField
                    variant="outlined"
                    label="Search"
                    size="small"
                    className="Search-text-fields"
                    onChange={handleSearchFilter}
                  />
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    color="secondary"
                    className="Filter-button"
                    onClick={fetchPerdiemData}
                  >
                    Search
                  </Button>
                  <Button
                    variant={filtersExpanded ? "contained" : "outlined"}
                    startIcon={<FilterAltIcon />}
                    color="primary"
                    className="Filter-button"
                    onClick={() => setFiltersExpanded(!filtersExpanded)}
                  >
                    {filtersExpanded ? "Hide Filters" : "Show Filters"}
                  </Button>
                </div>
                {filtersExpanded && (
                  <div className="Filters-div">
                    <Grid container>
                      <Grid
                        item
                        lg={3}
                        md={6}
                        className="Filter-grid-item"
                      >
                        <Autocomplete
                          size="small"
                          variant="standard"
                          noOptionsText="No options"
                          value={emailFilter}
                          disablePortal
                          options={employees.filter(
                            (emp) => emp.workEmail !== null
                          )}
                          getOptionLabel={(option) => option.workEmail}
                          onChange={handleEmailFilter}
                          renderOption={(props, option) => (
                            <Box {...props} key={option.workEmail}>
                              {option.workEmail}
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField {...params} label="Traveller's Email" />
                          )}
                        />
                      </Grid>
                      <Grid
                        item
                        lg={3}
                        md={6}
                        className="Filter-grid-item"
                      >
                        <Autocomplete
                          size="small"
                          disablePortal
                          options={currencyOptions}
                          getOptionLabel={(option) => option.toString()}
                          value={currencyFilter}
                          onChange={(_, newValue) => {
                            setCurrencyFilter(newValue);
                          }}
                          renderOption={(props, option) => (
                            <Box {...props} key={option}>
                              {option}
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Converted Currency"
                              InputProps={{
                                ...params.InputProps,
                                readOnly: true,
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid
                        item
                        lg={3}
                        md={6}
                        className="Filter-grid-item"
                      >
                        <Autocomplete
                          size="small"
                          disablePortal
                          options={perdiemPaymentNetsuiteStatusArray}
                          value={statusFilter}
                          getOptionLabel={(status) => status}
                          onChange={(event, newValue) => {
                            setStatusFilter(newValue);
                          }}
                          renderOption={(props, status) => (
                            <Box {...props}>
                              <span
                                className="Status-dot-dropdown"
                                style={{
                                  backgroundColor:
                                    getPerdiemPaymentNetSuiteStatusInfo(status),
                                }}
                              ></span>
                              {status}
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField {...params} label="NetSuite Status" />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </div>
                )}
              </div>

              {isAccessGranted([PRIVILEGES.PUSH_PER_DIEM_PAYMENTS_TO_NS]) && (
                <Grid container>
                  <Grid item lg={12} className="Button-grid-netsuite">
                    <Button
                      variant="contained"
                      size="small"
                      color="secondary"
                      className="Push-to-netsuite-button"
                      startIcon={<CurrencyExchangeIcon />}
                      disabled={selectedPayments.length === 0}
                      onClick={handlePushPerdiemPaymentsToNS}
                    >
                      Push Payments to NS
                      {selectedPayments.length > 0 && (
                        <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                          ({selectedPayments.length})
                        </span>
                      )}
                    </Button>
                  </Grid>
                </Grid>
              )}

              {isTableLoading && <LinearProgress color="secondary" />}

              <div>
                <TableContainer className="Table-container">
                  <Table size={"small"}>
                    <EnhancedTableHead
                      headCells={headCells}
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {stableSort(payments, getComparator(order, orderBy))
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row, key) => {
                          return (
                            <TableRow hover tabIndex={-1} key={key}>
                              <TableCell
                                className="Table-cell Table-cell-checkbox"
                                padding="checkbox"
                              >
                                <Checkbox
                                  color="secondary"
                                  onChange={() =>
                                    togglePaymentSelection(row.id)
                                  }
                                  disabled={isSelectDisabled(row)}
                                  checked={
                                    isSelectDisabled(row)
                                      ? true
                                      : selectedPayments.includes(row.id)
                                  }
                                />
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-per-diem-number">
                                {row.perDiemNumber}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-per-diem-number">
                                {row.travellerEmail}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-per-diem-number">
                                {formatDate(row.addedDate)}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-currency">
                                {row.paymentCurrency}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-cost">
                                {row.paymentAmount ? row.paymentAmount : "-"}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-currency">
                                {row.convertedCurrency
                                  ? row.convertedCurrency
                                  : "-"}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-cost">
                                {row.convertedAmount
                                  ? formatAmount(row.convertedAmount)
                                  : "-"}
                              </TableCell>
                              <TableCell className="Table-cell Table-cell-net-suite-status">
                                {getNetSuiteStatus(row)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>

              {payments.length === 0 && (
                <div className="No-payments-label">No Per Diem Payments</div>
              )}

              {isTablePaginating && <LinearProgress color="secondary" />}
              <TablePagination
                className="Table-pagination"
                rowsPerPageOptions={
                  window.innerHeight >= 850 ? [5, 12, 15] : [5, 10, 15]
                }
                component="div"
                count={-1}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default AllPerDiemPayments;
